import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  IPatientTreatmentError,
  ITreatmentSummary,
} from "../../../@types/admin/AdminTreatment";
import { TableSorted } from "../../../components/TableSorted";
import { usePatientTreatmentErrors } from "../../../hooks/queries/admin/treatments";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  ModalContainer,
  ModalTitle,
  ModalHeader,
  ModalSubtitle,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";

const SEARCH_FIELDS: (keyof IPatientTreatmentError)[] = [
  "errorCode",
  "errorDescription",
  "serialNumber",
  "occurredAt",
];

interface ITreatmentErrorsModalProps {
  patient: ITreatmentSummary;
}

/**
 * One patient's nebulizer error log — Retool's `modalTreatmentErrors`
 * (EN-5156), opened by selecting a row in the summary table.
 *
 * The patient columns Retool repeated in this table (patient id, name,
 * email) are dropped: the modal already names the patient it belongs to, so
 * repeating them on every row was noise inherited from the flat export.
 */
export function TreatmentErrorsModal({ patient }: ITreatmentErrorsModalProps) {
  const { treatmentErrors, isLoading, isError } = usePatientTreatmentErrors(
    patient.patientId
  );

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: treatmentErrors,
    searchFields: SEARCH_FIELDS,
  });

  const columns: ColumnDef<IPatientTreatmentError, string>[] = useMemo(
    () => [
      {
        accessorKey: "occurredAt",
        header: "Occurred at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.occurredAt, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "errorCode",
        header: "Error code",
        cell: ({ row }) => <span>{row.original.errorCode}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "errorDescription",
        header: "Error description",
        enableSorting: true,
      },
      {
        accessorKey: "serialNumber",
        header: "Serial number",
        enableSorting: true,
      },
    ],
    []
  );

  const patientName =
    [patient.firstName, patient.lastName].filter(Boolean).join(" ") ||
    `Patient ${patient.patientId}`;

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Treatment error logs</ModalTitle>
        <ModalSubtitle>
          {patientName} · patient ID {patient.patientId}
          {patient.serialNumber ? ` · device ${patient.serialNumber}` : ""}
        </ModalSubtitle>
      </ModalHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by error code, description, or serial number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load this patient&apos;s treatment errors. Close the dialog
          and try again.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-treatment-errors"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "treatment-errors",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </ModalContainer>
  );
}
